import { QuoteCardsEditorStore } from '@src/editor/QuoteCardsEditorStore';
import { EditorChangeType } from '@src/editor/types';
import { createCompositionElement } from '@src/services/composition/CompositionElementService';
import { useAssetPrewarmingService } from '@src/services/ServiceContainerContext';
import { WarmedRenderer } from '@src/services/WarmedRenderer';
import { useVideoCreatorStore } from '@src/stores-v2';
import { useAnalytics } from '@src/stores-v2/StoreContext';
import { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import { AspectRatio } from '@src/types.ts/video';
import {
  getAspectRatioFromSource,
  getDimensionSize,
  modifyCompositionProperty,
  resolveElementFromPath,
} from '@src/utility/compositionSettings';
import { LogManager } from '@src/utility/Logger';
import { canFillSpeakerHeadshot } from '@src/utility/quoteCardHeadshot';
import { adjustBackgroundImageAspectRatio } from '@src/utility/quoteCardsCompositionSettings';
import { convertFromPixels } from '@src/utility/videoTranscriptionUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type CompositionTemplate,
  type ControlConfiguration,
  QuoteControlType,
} from '../../sidepanel/CompositionSettings/types';

import type { TabType } from '@src/components/content-components/QuoteCard/QuoteCardTabs';
import type { ElementState } from '@src/renderer/ElementState';
import type { RendererState } from '@src/renderer/RendererState';
import type { ExtendedAspectRatio, VideoSource } from '@src/types.ts/video';

const logger = LogManager.getLogger('hook:useQuoteCardRenderer');

const DEFAULT_STILL_TIME = 2.5;

/**
 * Recursively traverses all elements (including nested compositions up to 2 levels)
 * and computes the time at which all animations have completed.
 * Returns `element.time + animation.time + animation.duration` for the latest-ending animation.
 */
function calculatePostAnimationTime(
  elements: Record<string, unknown>[],
  parentTime: number = 0,
): number {
  let maxEnd = 0;

  for (const el of elements) {
    const elTime = (typeof el.time === 'number' ? el.time : 0) + parentTime;

    const animations = el.animations;
    if (Array.isArray(animations)) {
      const animObjects = animations.filter(
        (item): item is Record<string, unknown> =>
          typeof item === 'object' && item != null,
      );
      for (const anim of animObjects) {
        const animStart = typeof anim.time === 'number' ? anim.time : 0;
        const animDuration =
          typeof anim.duration === 'number' ? anim.duration : 0;
        maxEnd = Math.max(maxEnd, elTime + animStart + animDuration);
      }
    }

    // Recurse into nested composition elements (up to 2 levels deep)
    const nested = el.elements;
    if (Array.isArray(nested)) {
      const nestedElements = nested.filter(
        (item): item is Record<string, unknown> =>
          typeof item === 'object' && item != null,
      );
      maxEnd = Math.max(
        maxEnd,
        calculatePostAnimationTime(nestedElements, elTime),
      );
    }
  }

  return maxEnd;
}

function getStillFrameTime(
  source: Record<string, unknown> | undefined,
): number {
  if (!source?.elements || !Array.isArray(source.elements)) {
    return DEFAULT_STILL_TIME;
  }
  const computed = calculatePostAnimationTime(source.elements);
  return computed > 0 && computed < 4 ? computed : DEFAULT_STILL_TIME;
}

/**
 * Extract current text/name/org values from the renderer source using the template's
 * control mappings. Used to carry over user edits when switching to a new template.
 */
function extractCurrentQuoteValues(
  source: Record<string, any> | undefined,
  controls: ControlConfiguration[] | undefined,
  templateSource: Record<string, any> | undefined,
): { text?: string; name?: string; org?: string; headshot?: string } {
  const result: {
    text?: string;
    name?: string;
    org?: string;
    headshot?: string;
  } = {};
  if (!source || !controls) return result;

  // The root composition element is the first element in the source
  const rootElement = source?.elements?.[0];
  if (!rootElement) return result;

  for (const control of controls) {
    const quoteType = control.default?.quote;
    if (
      quoteType !== QuoteControlType.TEXT &&
      quoteType !== QuoteControlType.NAME &&
      quoteType !== QuoteControlType.ORG &&
      quoteType !== QuoteControlType.HEADSHOT
    ) {
      continue;
    }

    // Read the current value from the first element path
    const path = control.elementPaths?.[0];
    if (!path) continue;

    const element = resolveElementFromPath(rootElement, path);
    if (!element) continue;

    const value = (element as Record<string, any>)[control.property];
    if (typeof value !== 'string') continue;

    if (quoteType === QuoteControlType.TEXT) result.text = value;
    else if (quoteType === QuoteControlType.NAME) result.name = value;
    else if (quoteType === QuoteControlType.ORG) result.org = value;
    // A photo on the card carries over; the template's placeholder does not.
    else if (
      templateSource &&
      !canFillSpeakerHeadshot(
        rootElement,
        templateSource,
        path,
        control.property,
      )
    )
      result.headshot = value;
  }

  return result;
}

type BaseQuoteCardRendererParams = {
  text: string;
  background?: string;
  name?: string;
  org?: string;
  headshot?: string;
  compositionTemplate: CompositionTemplate;
  quoteCardSource?: Record<string, any>;
};

type UseQuoteCardRendererParams =
  | (BaseQuoteCardRendererParams & {
      mode?: 'player';
      onClick?: () => void;
      hoverOverlay?: boolean;
      setActiveTab?: never;
    })
  | (BaseQuoteCardRendererParams & {
      mode: 'interactive';
      onClick?: never;
      hoverOverlay?: never;
      setActiveTab?: (tab: TabType) => void;
    });

const baseSource: VideoSource = {
  elements: [],
  width: 1080,
  height: 1080,
  duration: 4,
  output_format: 'gif',
};

/**
 * Custom hook to initialize and manage Creatomate renderer for quote cards
 */
export const useQuoteCardRenderer = ({
  text,
  background,
  name,
  org,
  headshot,
  mode = 'player',
  compositionTemplate,
  quoteCardSource,
  onClick,
  hoverOverlay = false,
  setActiveTab,
}: UseQuoteCardRendererParams) => {
  const analyticsStore = useAnalytics();
  const videoCreator = useVideoCreatorStore();
  const assetPrewarmingService = useAssetPrewarmingService();
  const rendererContainerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<WarmedRenderer | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [compositionReady, setCompositionReady] = useState<boolean>(false);
  // Local stores (only instantiated for interactive mode)
  const videoSourceStoreRef = useRef<VideoSourceStore | null>(null);
  const editorStoreRef = useRef<QuoteCardsEditorStore | null>(null);
  const [activeElement, setActiveElement] = useState<ElementState | null>(null);
  const [activeComposition, setActiveComposition] =
    useState<ElementState | null>(null);
  const [currentAspectRatio, setCurrentAspectRatio] =
    useState<ExtendedAspectRatio>(AspectRatio.AR_1_1);
  const [activeCompositionTemplate, setActiveCompositionTemplate] =
    useState<CompositionTemplate>(compositionTemplate);

  // Bumped when the canvas is rebuilt from a template; adoptTemplate is not a
  // rebuild, so it doesn't re-apply the speaker headshot (PLAT-11977).
  const [canvasRevision, setCanvasRevision] = useState(0);

  // The template this hook holds is what the settings panel diffs against and
  // what "Set as Graphic Standard" writes back. After a save (Set as Graphic
  // Standard or Save New Template) the modal adopts the saved record, so the
  // next save starts from it and the card autosaves under it (PLAT-11977).
  const adoptTemplate = useCallback((template: CompositionTemplate) => {
    setActiveCompositionTemplate(template);
  }, []);

  // Cache renderer source per template ID so switching back restores state
  const sourceByTemplateRef = useRef<Map<string, Record<string, any>>>(
    new Map(),
  );

  // Track current quote card text values so user edits carry over to new templates
  const currentTextRef = useRef(text);
  const currentNameRef = useRef(name);
  const currentOrgRef = useRef(org);

  // Keep headshot in a ref so callbacks always have the latest value
  // (avoids stale closures in addCompositionElement/replaceCompositionElement)
  const headshotRef = useRef(headshot);
  headshotRef.current = headshot;
  // The headshot on the canvas when the user switched templates (e.g. after
  // picking another speaker); wins over the card's speaker headshot.
  const currentHeadshotRef = useRef<string | undefined>(undefined);

  // Track undo/redo availability for reactive UI updates
  const [canUndo, setCanUndo] = useState<boolean>(false);
  const [canRedo, setCanRedo] = useState<boolean>(false);
  // Bumps on every editor change (a drag or resize on the canvas included) so
  // React consumers that read the renderer source can recompute; `canUndo`
  // alone stays true from the second edit on and would not re-render them.
  const [editRevision, setEditRevision] = useState<number>(0);

  // Time at which all animations have completed (for showing still frame)
  const [stillFrameTime, setStillFrameTime] =
    useState<number>(DEFAULT_STILL_TIME);

  // Track previous renderer state for external change detection
  const prevStateRef = useRef<RendererState | undefined>(undefined);
  // Track if renderer is playing to know when to sync external changes
  const isPlayingRef = useRef<boolean>(false);
  // Track iframe active state for deactivation handling
  const isIframeActiveRef = useRef<boolean>(false);

  // Register ALL non-archived brand font variants (plus the standard web fonts)
  // in the source-level `fonts[]` before loading it into the editor. This mirrors
  // Creator Studio's render path (`CustomFontsService.getCustomFontsSource`). A
  // saved quote-card source only carries the *authored* fonts, so when a user
  // picks a different variant in the Font control its postScriptName has no
  // registered face and the canvas silently falls back (e.g. an italic pick
  // rendering upright). `getCustomFontsSource` merges (it keeps any existing
  // fonts), and `Timeline.toRenderJSON` preserves `source.fonts`, so injecting
  // once at load flows through every subsequent `refreshTimeline`.
  const injectBrandFonts = useCallback(
    (source: Record<string, any>): VideoSource =>
      (videoCreator.customFontsService?.getCustomFontsSource(
        source,
      ) as VideoSource) ?? (source as VideoSource),
    [videoCreator],
  );

  useEffect(() => {
    if (!rendererContainerRef.current) return;
    const renderer = new WarmedRenderer(
      rendererContainerRef.current,
      mode,
      // Arbor Active Selection project, uses feature flag that allows to
      // re-select elements on top of currently active element
      'public-z3d8bimbdn24r8vnzflj5uqv',
      assetPrewarmingService,
    );
    rendererRef.current = renderer;
    // this is needed to dive into a composition and not allow to move the whole composition as one component here
    renderer.onStateChange = state => {
      // Sync external changes from iframe when user manually drags/resizes elements
      // Only sync when not playing and iframe is active (user clicked into it)
      if (
        mode === 'interactive' &&
        !isPlayingRef.current &&
        renderer.isIframeActive() &&
        editorStoreRef.current
      ) {
        editorStoreRef.current.syncExternalChanges(prevStateRef.current, state);
      }

      // Store current state as previous for next comparison
      prevStateRef.current = state;

      // Update active element to match current state
      // We need to update this on every state change so controls reflect the current values
      // If we have a previous activeElement, try to find it in the new state by ID
      setActiveElement(prev => {
        if (prev && state?.elements) {
          // Try to find the same element in the new state by source.id
          const findElementById = (
            elements: ElementState[],
            targetId: string,
          ): ElementState | null => {
            for (const el of elements) {
              if (el?.source?.id === targetId) return el;
              if (el?.elements) {
                const found = findElementById(el.elements, targetId);
                if (found) return found;
              }
            }
            return null;
          };

          const updatedElement = findElementById(
            state.elements,
            prev.source?.id,
          );
          if (updatedElement) return updatedElement;
        }
        // Fall back to first element
        return state?.elements?.[0] ?? null;
      });

      const compositionElement = state?.elements?.[0];
      if (
        compositionElement?.elements?.length === 1 &&
        compositionElement?.elements?.[0]?.source?.type === 'composition'
      ) {
        renderer.setActiveComposition(
          compositionElement?.elements?.[0]?.source?.id,
        );
        setActiveComposition(compositionElement?.elements?.[0]);
      } else {
        renderer.setActiveComposition(compositionElement?.source?.id);
        setActiveComposition(compositionElement);
      }
    };

    renderer.onActiveElementsChange = elementIds => {
      if (setActiveTab && elementIds.length > 0) {
        setActiveTab('settings');
      }
      const activeElement = renderer.findElement(
        element => element.source.id === elementIds[0],
      );
      if (activeElement && elementIds.length === 1) {
        setActiveElement(activeElement);
        return;
      }
      let fallbackActiveElement = renderer.getElements()?.[0];
      if (
        fallbackActiveElement?.elements?.length === 1 &&
        fallbackActiveElement?.elements?.[0]?.source?.type === 'composition'
      ) {
        fallbackActiveElement = fallbackActiveElement?.elements?.[0];
      }
      setActiveElement(fallbackActiveElement || null);
    };

    // this prevents entering svg edit mode
    renderer.onToolChange = () => {
      const source = renderer.getSource();
      const activeElement = source?.elements?.[0];
      if (activeElement?.elements?.[0]?.type === 'composition') {
        renderer.setActiveElements([activeElement.elements?.[0]?.id]);
      } else {
        renderer.setActiveElements([activeElement?.id]);
      }
    };

    // Track play/pause state for external change detection
    renderer.onPlay = () => {
      isPlayingRef.current = true;
    };

    renderer.onPause = () => {
      isPlayingRef.current = false;
    };

    // Setup iframe deactivation handler for batched external changes
    const checkIframeActive = () => {
      if (!renderer) return;

      const wasActive = isIframeActiveRef.current;
      const isCurrentlyActive = renderer.isIframeActive();
      isIframeActiveRef.current = isCurrentlyActive;

      // Detect transition from active -> inactive (user finished editing)
      if (wasActive && !isCurrentlyActive && editorStoreRef.current) {
        logger.info(
          'Quote Card iframe deactivated - dispatching FORCED_REFRESH',
        );
        // Dispatch event to trigger refresh through proper editor observer pattern
        editorStoreRef.current.editor.notifyObservers(
          EditorChangeType.FORCED_REFRESH,
          { reason: 'iframe_deactivated' },
        );
      }
    };

    // Check on both window focus/blur and document focus/blur
    window.addEventListener('focus', checkIframeActive, true);
    window.addEventListener('blur', checkIframeActive, true);
    document.addEventListener('focusin', checkIframeActive);
    document.addEventListener('focusout', checkIframeActive);

    // Wait for renderer to be ready
    // Guard: onReady can fire again when the iframe regains visibility (e.g. tab
    // switch). Only run the full initialization once; subsequent calls are no-ops.
    renderer.onReady = async () => {
      if (videoSourceStoreRef.current) {
        logger.debug(
          'Renderer onReady fired again — skipping re-initialization',
        );
        return;
      }

      // Hide controls for cleaner view
      await renderer.setZoom('auto');
      await renderer.setLoop(false);
      await renderer.setCacheBypassRules([]);

      await renderer.setControls(false);

      videoSourceStoreRef.current = new VideoSourceStore(analyticsStore);
      videoSourceStoreRef.current.connectRenderer(renderer);

      editorStoreRef.current = new QuoteCardsEditorStore(
        videoSourceStoreRef.current,
      );
      let stillTime = DEFAULT_STILL_TIME;
      if (
        quoteCardSource &&
        quoteCardSource?.elements?.[0]?.type === 'composition'
      ) {
        editorStoreRef.current.loadVideoSource(
          injectBrandFonts({
            ...quoteCardSource,
            duration: 4,
          }),
        );
        setCurrentAspectRatio(getAspectRatioFromSource(quoteCardSource));
        stillTime = getStillFrameTime(quoteCardSource);
        setStillFrameTime(stillTime);
        // Seed the per-template source cache with the initial saved source
        sourceByTemplateRef.current.set(
          compositionTemplate.id,
          quoteCardSource,
        );

        setCompositionReady(true);
      } else {
        editorStoreRef.current.loadVideoSource(injectBrandFonts(baseSource));
        stillTime = getStillFrameTime(baseSource);
        setStillFrameTime(stillTime);
      }
      rendererRef.current?.setTime(stillTime).then(() => {
        setIsReady(true);
      });
    };

    // Cleanup on unmount
    return () => {
      // Remove event listeners
      window.removeEventListener('focus', checkIframeActive, true);
      window.removeEventListener('blur', checkIframeActive, true);
      document.removeEventListener('focusin', checkIframeActive);
      document.removeEventListener('focusout', checkIframeActive);

      if (videoSourceStoreRef.current) {
        videoSourceStoreRef.current.disconnectRenderer();
        videoSourceStoreRef.current = null;
      }
      setCompositionReady(false);
      setCanUndo(false);
      setCanRedo(false);
      if (editorStoreRef.current) {
        editorStoreRef.current = null;
      }
      if (rendererRef.current) {
        // WarmedRenderer.dispose() override evicts cache tracking.
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
      // Clear state tracking refs
      prevStateRef.current = undefined;
      isPlayingRef.current = false;
      isIframeActiveRef.current = false;
      setStillFrameTime(DEFAULT_STILL_TIME);
    };
    // `assetPrewarmingService` is included because `new WarmedRenderer(...,
    // assetPrewarmingService)` reads it. It's a tree-lifetime singleton in
    // practice, so this dep doesn't cause real re-mounts.
  }, [mode, assetPrewarmingService]);

  useEffect(() => {
    if (
      !isReady ||
      !editorStoreRef.current ||
      !compositionTemplate ||
      compositionReady
    )
      return;

    addCompositionElement(compositionTemplate);
  }, [isReady]);

  // Set up undo/redo observer only after composition is ready
  useEffect(() => {
    if (mode !== 'interactive' || !compositionReady || !editorStoreRef.current)
      return;

    // Create observer object that we can unregister later
    const undoRedoObserver = {
      onEditorChange: () => {
        if (editorStoreRef.current?.editor) {
          setCanUndo(editorStoreRef.current.editor.canUndo());
          setCanRedo(editorStoreRef.current.editor.canRedo());
          setEditRevision(r => r + 1);
        }
      },
    };

    // Set up observer to update undo/redo availability reactively
    editorStoreRef.current.editor.registerObserver(undoRedoObserver);

    // Set initial undo/redo state
    setCanUndo(editorStoreRef.current.editor.canUndo());
    setCanRedo(editorStoreRef.current.editor.canRedo());

    // Cleanup: unregister observer when effect runs again or on unmount
    return () => {
      if (editorStoreRef.current?.editor) {
        editorStoreRef.current.editor.unregisterObserver(undoRedoObserver);
      }
    };
  }, [compositionReady]);

  // When the headshot URL arrives after the composition is already created,
  // update the headshot element source directly via the editor.
  // Also re-applies when the canvas is rebuilt from another template, since the
  // new composition may not carry the headshot over.
  useEffect(() => {
    if (!headshot || !compositionReady || !editorStoreRef.current) return;

    const template = activeCompositionTemplate;
    const headshotControl = template?.controls?.find(
      c => c.default?.quote === QuoteControlType.HEADSHOT,
    );
    if (!headshotControl) return;

    // Look up the composition element from the store (synchronous, always current)
    const compositionEl = videoSourceStoreRef.current?.findElement(
      el => el.type === 'composition',
    );
    const elementId = compositionEl?.id;
    if (!elementId) return;

    // Fill only; never over a photo the user picked or the card was saved with.
    const path = headshotControl.elementPaths[0];
    if (
      path &&
      !canFillSpeakerHeadshot(
        compositionEl,
        template.source,
        path,
        headshotControl.property,
      )
    )
      return;

    modifyCompositionProperty(
      elementId,
      editorStoreRef.current,
      headshotControl.elementPaths,
      headshotControl.property,
      headshot,
      false,
    );
  }, [headshot, compositionReady, canvasRevision]);

  const updateAspectRatio = useCallback(
    async (dimension: ExtendedAspectRatio) => {
      if (!videoSourceStoreRef.current) return;
      const dimensions = getDimensionSize(dimension);
      const compositionEl = videoSourceStoreRef.current.findElement(
        el => el.type === 'composition',
      );

      const nestedCompositionEl =
        compositionEl?.elements?.[0]?.elements?.length === 1 &&
        compositionEl?.elements?.[0]?.type === 'composition'
          ? compositionEl?.elements?.[0]?.elements?.[0]
          : null;

      const elementUpdates = new Map<string, Record<string, any>>();
      if (compositionEl) {
        elementUpdates.set(compositionEl.id, {
          width: '100%',
          height: '100%',
        });
      }
      if (nestedCompositionEl) {
        elementUpdates.set(nestedCompositionEl.id, {
          width: '100%',
          height: '100%',
        });
      }

      const success =
        editorStoreRef.current?.editor.commandUpdateTimelineAndElements(
          { width: dimensions.width, height: dimensions.height },
          elementUpdates,
          false,
        );

      if (success) {
        setCurrentAspectRatio(dimension);
      }
    },
    [],
  );

  useEffect(() => {
    if (mode !== 'player' || !compositionReady) return;

    const container = rendererContainerRef.current;
    if (!container) return;

    // Create a transparent overlay to capture mouse events over the iframe
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.zIndex = '1';
    overlay.style.pointerEvents = 'auto';
    overlay.style.backgroundColor = hoverOverlay
      ? 'rgba(0, 0, 0, 0.2)'
      : 'rgba(0, 0, 0, 0)';
    overlay.style.transition = 'background-color 0.2s ease';
    overlay.style.cursor = 'pointer';

    const handleMouseEnter = () => {
      if (hoverOverlay) {
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0)';
      }
      rendererRef.current?.setTime(0);
      rendererRef.current?.play();
    };

    const handleMouseLeave = () => {
      if (hoverOverlay) {
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
      }
      rendererRef.current?.pause();
      rendererRef.current?.setTime(stillFrameTime);
    };

    // wait for the composition to be inserted to canvas otherwise if I hover the quote card before it is inserted then time sets to 0 but play not calls
    const timeoutId = setTimeout(() => {
      overlay.addEventListener('mouseenter', handleMouseEnter);
      overlay.addEventListener('mouseleave', handleMouseLeave);
      if (onClick) {
        container.addEventListener('click', onClick);
      }
      container.appendChild(overlay);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      overlay.removeEventListener('mouseenter', handleMouseEnter);
      overlay.removeEventListener('mouseleave', handleMouseLeave);
      if (onClick) {
        container.removeEventListener('click', onClick);
      }
      // Only remove if it's actually a child
      if (overlay.parentNode === container) {
        container.removeChild(overlay);
      }
    };
  }, [compositionReady]);

  const preview = useCallback(() => {
    rendererRef.current?.setTime(0);
    rendererRef.current?.play();
    setTimeout(() => {
      rendererRef.current?.setTime(stillFrameTime);
      rendererRef.current?.pause();
    }, 4000);
  }, [rendererRef, stillFrameTime]);

  const addCompositionElement = useCallback(
    (composition: CompositionTemplate, createUndoEntry: boolean = false) => {
      if (!editorStoreRef.current) return;

      const aspectRatio = getAspectRatioFromSource(composition.source);

      createCompositionElement({
        template: composition,
        editorStore: editorStoreRef.current,
        videoProps: {
          time: 0,
          duration: 4,
          getCurrentVideo: () => baseSource,
          aspectRatio: aspectRatio,
          organization: videoCreator.organization,
          story: videoCreator?.story,
          convertToVh: (v: string) =>
            convertFromPixels(parseFloat(v || '32'), 'vh', {
              width: baseSource.width,
              height: baseSource.height,
            }) + ' vh',
        },
        upsertExtraElementDataCb: () => {},
        quoteCardProps: {
          text: currentTextRef.current,
          background: background,
          name: currentNameRef.current,
          org: currentOrgRef.current,
          headshot: currentHeadshotRef.current ?? headshotRef.current,
          dimension: aspectRatio,
        },
        createUndoEntry,
      }).then(async resp => {
        if (resp.ok) {
          setStillFrameTime(getStillFrameTime(composition.source));
          setCompositionReady(true);
          setActiveCompositionTemplate(composition);
          setCanvasRevision(r => r + 1);
          setCurrentAspectRatio(aspectRatio);

          const elementUpdates = new Map<string, Record<string, any>>();
          elementUpdates.set(resp.value.elementId, { fill_color: '#000000' });
          if (
            typeof composition.source.width !== 'number' ||
            typeof composition.source.height !== 'number'
          ) {
            logger.error('Unexpected composition dimensions type', {
              composition,
            });
            return;
          }
          editorStoreRef.current?.editor.commandUpdateTimelineAndElements(
            {
              width: composition.source.width,
              height: composition.source.height,
            },
            elementUpdates,
            false,
          );

          const control = composition.controls?.find(
            control => control.default?.quote === QuoteControlType.BACKGROUND,
          );
          if (control) {
            const results = await adjustBackgroundImageAspectRatio(
              composition.source,
              control,
              background as string,
            );
            if (results) {
              for (const result of results) {
                editorStoreRef.current?.editor.commandUpdateElement(
                  result.elementId,
                  {
                    width: result.width,
                    height: result.height,
                  },
                  false,
                );
              }
            }
          }
        }
      });
    },
    [editorStoreRef.current],
  );

  const replaceCompositionElement = useCallback(
    (composition: CompositionTemplate) => {
      if (!editorStoreRef.current) return;

      // Cache current source before switching so we can restore it later
      const currentSource = rendererRef.current?.getSource();
      if (currentSource && activeCompositionTemplate?.id) {
        sourceByTemplateRef.current.set(
          activeCompositionTemplate.id,
          currentSource,
        );

        // Extract user-edited text/name/org so they carry over to new templates
        const editedValues = extractCurrentQuoteValues(
          currentSource,
          activeCompositionTemplate.controls,
          activeCompositionTemplate.source,
        );
        if (editedValues.text !== undefined)
          currentTextRef.current = editedValues.text;
        if (editedValues.name !== undefined)
          currentNameRef.current = editedValues.name;
        if (editedValues.org !== undefined)
          currentOrgRef.current = editedValues.org;
        if (editedValues.headshot !== undefined)
          currentHeadshotRef.current = editedValues.headshot;
      }

      // Restore cached source if available, otherwise build fresh
      const cachedSource = sourceByTemplateRef.current.get(composition.id);
      if (cachedSource?.elements?.[0]?.type === 'composition') {
        editorStoreRef.current.loadVideoSource(
          injectBrandFonts({
            ...cachedSource,
            duration: 4,
          }),
        );
        setActiveCompositionTemplate(composition);
        setCanvasRevision(r => r + 1);
        setCurrentAspectRatio(getAspectRatioFromSource(cachedSource));
      } else {
        editorStoreRef.current.loadVideoSource(injectBrandFonts(baseSource));
        addCompositionElement(composition);
      }

      editorStoreRef.current.editor.clearHistory();
      const stillTime = getStillFrameTime(cachedSource || composition.source);
      setStillFrameTime(stillTime);
      rendererRef.current?.setTime(stillTime);
    },
    [editorStoreRef.current, activeCompositionTemplate],
  );

  const setDefaultActiveElement = useCallback(() => {
    if (!rendererRef.current) return;
    const state = rendererRef.current.state;

    const compositionElement = state?.elements?.[0];
    if (
      compositionElement?.elements?.length === 1 &&
      compositionElement?.elements?.[0]?.source?.type === 'composition'
    ) {
      rendererRef.current.setActiveComposition(
        compositionElement?.elements?.[0]?.source?.id,
      );
      setActiveComposition(compositionElement?.elements?.[0] || null);
      rendererRef.current.setActiveElements([
        compositionElement?.elements?.[0]?.elements?.[0]?.source?.id,
      ]);
      setActiveElement(compositionElement?.elements?.[0]);
    } else {
      rendererRef.current.setActiveComposition(compositionElement?.source?.id);
      setActiveComposition(compositionElement || null);
      rendererRef.current.setActiveElements([compositionElement?.source?.id]);
      setActiveElement(compositionElement || null);
    }
  }, [rendererRef]);

  return {
    rendererContainerRef,
    rendererRef,
    editorStoreRef,
    activeElement,
    activeComposition,
    updateAspectRatio,
    preview,
    isReady,
    currentAspectRatio,
    canUndo,
    canRedo,
    editRevision,
    replaceCompositionElement,
    setDefaultActiveElement,
    activeCompositionTemplate,
    adoptTemplate,
    compositionReady,
  };
};
